<template>
  <el-dialog
    title="二次登录验证"
    :visible.sync="visible"
    width="480px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="two-factor-login-dialog-shell"
    @closed="onClosed"
  >
    <!-- Step 1：扫码 -->
    <div v-show="step === 1" class="tf-step tf-step--qr">
      <div class="tf-qrcode-card">
        <img v-if="qrSrc" class="tf-qrcode-img" :src="qrSrc" alt="二次登录二维码" />
        <div v-else class="tf-qrcode-placeholder">
          <span class="tf-qrcode-placeholder__hint">二维码</span>
          <span class="tf-qrcode-placeholder__sub" @click="getCode">获取失败，重新获取</span>
        </div>
      </div>
      <p class="tf-qr-tip">请使用验证器 APP 扫码登录</p>
    </div>

    <!-- Step 2：验证码 -->
    <div v-show="step === 2" class="tf-step tf-step--code">
      <h3 class="tf-code-heading">输入验证码</h3>
      <p class="tf-code-desc">我们已向您发送了一个 <strong>6</strong> 位验证码，请在下方输入该验证码。</p>
      <div class="tf-digit-row">
        <input
          v-for="idx in digitIndexes"
          :key="'d-' + idx"
          :ref="'digitRef'"
          :value="digits[idx]"
          type="tel"
          inputmode="numeric"
          maxlength="1"
          class="tf-digit"
          autocomplete="one-time-code"
          @input="onDigitInput($event, idx)"
          @keydown="onDigitKeydown($event, idx)"
          @paste="onPasteDigit($event, idx)"
        />
      </div>
    </div>

    <span slot="footer" class="tf-footer-slot">
      <div v-if="step === 1" class="tf-footer tf-footer--single">
        <el-button type="primary" class="tf-btn-primary" @click="goNext">下一步</el-button>
      </div>
      <div v-else class="tf-footer tf-footer--split">
        <el-button class="tf-btn-plain" @click="goPrev">上一步</el-button>
        <el-button
          type="primary"
          class="tf-btn-primary"
          :loading="btnLoading"
          :class="{ 'disabled-btn': codeLength }"
          @click="submitBind"
          :disabled="codeLength || btnLoading"
          >验证</el-button
        >
      </div>
    </span>
  </el-dialog>
</template>

<script>
const CODE_LENGTH = 6;

export default {
  name: "TwoFactorLoginDialog",
  data() {
    return {
      visible: false,
      step: 1,
      qrSrc: "",
      digits: Array(CODE_LENGTH).fill(""),
      digitIndexes: Array.from({ length: CODE_LENGTH }, (_, i) => i),
      btnLoading: false
    };
  },
  computed: {
    // 判断输入框输入有没有完成
    codeLength() {
      const code = this.digits.join("");
      if (code.length !== CODE_LENGTH) {
        return true;
      }
      return false;
    }
  },
  mounted() {},
  methods: {
    // 获取谷歌绑定二维码
    getCode() {
      this.$http({
        url: "/user/bindGoogle",
        method: "GET"
      })
        .then((data) => {
          this.qrSrc = data.qrCodeUri;
        })
        .finally(() => {});
    },

    /**
     * @param {{ qrSrc?: string }} [payload] — qrSrc 为验证器二维码图片 URL（接口下发）
     */
    open(payload) {
      // this.getCode();
      this.step = 1;
      this.digits = Array(CODE_LENGTH).fill("");
      this.qrSrc = payload && payload.qrSrc ? payload.qrSrc : "";
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    onClosed() {
      this.step = 1;
      this.digits = Array(CODE_LENGTH).fill("");
      this.$emit("closed");
    },
    goNext() {
      this.step = 2;
      this.$nextTick(() => {
        const refs = this.$refs.digitRef;
        const first = Array.isArray(refs) ? refs[0] : refs;
        if (first && first.focus) first.focus();
      });
    },
    goPrev() {
      this.step = 1;
      this.digits = Array(CODE_LENGTH).fill("");
    },
    digitRefsList() {
      const refs = this.$refs.digitRef;
      return Array.isArray(refs) ? refs : refs ? [refs] : [];
    },
    onDigitInput(e, idx) {
      let v = (e.target.value || "").replace(/\D/g, "");
      if (v.length > 1) {
        v = v.slice(-1);
      }
      this.$set(this.digits, idx, v);
      e.target.value = v;
      if (v && idx < CODE_LENGTH - 1) {
        const list = this.digitRefsList();
        if (list[idx + 1]) list[idx + 1].focus();
      }
    },
    onDigitKeydown(e, idx) {
      const list = this.digitRefsList();
      if (e.key === "Backspace") {
        if (!this.digits[idx] && idx > 0) {
          e.preventDefault();
          list[idx - 1].focus();
          this.$set(this.digits, idx - 1, "");
          list[idx - 1].value = "";
        }
      }
      if (e.key === "ArrowLeft" && idx > 0) {
        list[idx - 1].focus();
      }
      if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) {
        list[idx + 1].focus();
      }
    },
    onPasteDigit(e, idx) {
      if (idx !== 0) return;
      const text = (e.clipboardData || window.clipboardData).getData("text") || "";
      const nums = text.replace(/\D/g, "").slice(0, CODE_LENGTH);
      if (!nums || this.step !== 2) return;
      e.preventDefault();
      const arr = nums.split("");
      for (let i = 0; i < CODE_LENGTH; i += 1) {
        this.$set(this.digits, i, arr[i] || "");
      }
      const list = this.digitRefsList();
      const focusIdx = Math.min(arr.length, CODE_LENGTH - 1);
      if (list[focusIdx]) list[focusIdx].focus();
      list.forEach((el, i) => {
        if (el) el.value = this.digits[i] || "";
      });
    },
    submitBind() {
      const code = this.digits.join("");
      if (code.length !== CODE_LENGTH) {
        this.$message.warning("请输入完整 6 位验证码");
        return;
      }
      this.btnLoading = true;
      this.$http({
        url: "/user/verifyGoogle?code=" + code,
        method: "POST"
      })
        .then((data) => {
          this.$message.success("验证成功");
          setTimeout(() => {
            this.close();
          }, 500);
        })
        .finally(() => {
          this.btnLoading = false;
        });
      this.$emit("bind", { code });
    }
  }
};
</script>

<style scoped lang="scss">
.tf-step {
  box-sizing: border-box;
}

.tf-step--qr {
  padding: 8px 0 4px;
}

.tf-qrcode-card {
  width: 220px;
  height: 220px;
  margin: 0 auto;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.tf-qrcode-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.tf-qrcode-placeholder {
  width: 100%;
  height: 100%;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #909399;
  font-size: 13px;
  text-align: center;
  padding: 12px;
  box-sizing: border-box;
}

.tf-qrcode-placeholder__hint {
  font-weight: 600;
  color: #606266;
}

.tf-qrcode-placeholder__sub {
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}

.tf-qr-tip {
  margin: 20px 0 0;
  text-align: center;
  font-size: 14px;
  color: #606266;
}

.tf-step--code {
  padding: 4px 0 8px;
}

.tf-code-heading {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.tf-code-desc {
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 1.65;
  color: #606266;

  strong {
    color: #303133;
    font-weight: 600;
  }
}

.tf-digit-row {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.tf-digit {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid #d6e4ff;
  background: #f5f9ff;
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  text-align: center;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
    background: #fff;
  }
}

.tf-footer-slot {
  display: block;
  width: 100%;
}

.tf-footer {
  display: flex;
  align-items: center;

  &--single {
    justify-content: center;
  }

  &--split {
    justify-content: space-between;
    width: 100%;
  }
}

.tf-btn-primary {
  min-width: 120px;
  border-radius: 8px;
  background-color: #0052d9 !important;
  border-color: #0052d9 !important;
}
.disabled-btn {
  border-radius: 4px;
  border: 0.5px solid rgba(0, 0, 0, 0.3) !important;
  background: rgba(53, 108, 227, 0.6) !important;
}
.tf-btn-plain {
  border-radius: 8px;
  background: #fff;
  border-color: #dcdfe6;
  color: #303133;
}
</style>

<style lang="scss">
.two-factor-login-dialog-shell.el-dialog {
  border-radius: 14px;
}

.two-factor-login-dialog-shell .el-dialog__header {
  padding: 18px 22px 12px;
  text-align: left;
}

.two-factor-login-dialog-shell .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.two-factor-login-dialog-shell .el-dialog__body {
  padding: 8px 22px 12px;
}

.two-factor-login-dialog-shell .el-dialog__footer {
  padding: 12px 22px 20px;
}
</style>
